import { useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import type { AuthContextType } from "../context/AuthContext.d";

// Hook personnalisÃ© pour les permissions e-commerce
export const useEcommercePermissions = () => {
  const authContext: AuthContextType = useAuth();
  const { user, hasPermission } = authContext;

  const hasSuperAdminAccess = Boolean(user?.is_superadmin);

  const canViewOrders = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.CommandesEnLigne.view")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canEditOrders = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.CommandesEnLigne.edit")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canProcessOrders = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.CommandesEnLigne.process")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canDeleteOrders = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.CommandesEnLigne.delete")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canCreateOrders = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.CommandesEnLigne.create")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canViewStores = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.Boutiques.view"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canCreateStores = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.Boutiques.create"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canEditStores = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.Boutiques.edit"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canDeleteStores = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.Boutiques.delete"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canActivateStores = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.Boutiques.activate"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canViewProducts = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.FichesProduit.view"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canCreateProducts = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.FichesProduit.create"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canEditProducts = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.FichesProduit.edit"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canDeleteProducts = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.FichesProduit.delete"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canPublishProducts = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.FichesProduit.publish"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canViewFrontend = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.Configuration.Frontend.view")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canEditFrontend = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.Configuration.Frontend.edit")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canConfigureFrontend = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission(
        "Gestion Commerciale.Ecommerce.Configuration.Frontend.configure"
      )
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canViewOnlineCustomers = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.ClientsEnLigne.view")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canCreateOnlineCustomers = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.ClientsEnLigne.create")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canEditOnlineCustomers = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.ClientsEnLigne.edit")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const canDeleteOnlineCustomers = useCallback(() => {
    return (
      hasSuperAdminAccess ||
      hasPermission("Gestion Commerciale.Ecommerce.ClientsEnLigne.delete")
    );
  }, [hasSuperAdminAccess, hasPermission]);

  const hasAnyEcommercePermission = useCallback(() => {
    if (hasSuperAdminAccess) return true;

    const ecommercePermissions = [
      "Gestion Commerciale.Ecommerce.CommandesEnLigne.view",
      "Gestion Commerciale.Ecommerce.Configuration.Boutiques.view",
      "Gestion Commerciale.Ecommerce.Configuration.FichesProduit.view",
      "Gestion Commerciale.Ecommerce.Configuration.Frontend.view",
      "Gestion Commerciale.Ecommerce.ClientsEnLigne.view",
    ];

    return ecommercePermissions.some((permission) => hasPermission(permission));
  }, [hasSuperAdminAccess, hasPermission]);

  const getPermissionErrorMessage = useCallback(
    (action: string, module: string) => {
      if (hasSuperAdminAccess) {
        return "Une erreur inattendue s'est produite.";
      }

      return `Vous n'avez pas la permission d'effectuer cette action (${action}) sur le module ${module}. Contactez votre administrateur pour obtenir les permissions nÃ©cessaires.`;
    },
    [hasSuperAdminAccess]
  );

  return {
    canViewOrders,
    canEditOrders,
    canProcessOrders,
    canDeleteOrders,
    canCreateOrders,
    canViewStores,
    canCreateStores,
    canEditStores,
    canDeleteStores,
    canActivateStores,
    canViewProducts,
    canCreateProducts,
    canEditProducts,
    canDeleteProducts,
    canPublishProducts,
    canViewFrontend,
    canEditFrontend,
    canConfigureFrontend,
    canViewOnlineCustomers,
    canCreateOnlineCustomers,
    canEditOnlineCustomers,
    canDeleteOnlineCustomers,
    hasAnyEcommercePermission,
    getPermissionErrorMessage,
    user,
    isSuperAdmin: hasSuperAdminAccess,
  };
};
